import React, {FC, useCallback, useEffect, useMemo, useState} from "react";
import {
    Button,
    Divider,
    Flex,
    FormControl,
    FormHelperText,
    FormLabel,
    Heading,
    Radio,
    RadioGroup,
    Stack,
    Text,
    useToast,
} from "@chakra-ui/react";
import {InactiveOrderReminderEmailEditor} from "./InactiveOrderReminderEmailEditor";
import {
    ApplicationSettingsRepositoryInstance,
    InactiveOrderReminderConfigAdminUpdateDto,
    InactiveOrderReminderMailAdminUpdateDto,
} from "../../repository/Application/ApplicationSettingsRepository";
import {
    PLAIN_SUBJECT_PREFIX,
    PLAIN_TEXT_AFTER,
    PLAIN_TEXT_BEFORE,
    splitInactiveOrderReminderEmailBodyToPlain,
    subjectTemplateToPlain,
} from "../../shared/utils/inactiveOrderReminderMailPlainText";
import {buildComposedEmailHtmlFromFormFields} from "../../shared/utils/inactiveOrderReminderEmailPreview";

const FALLBACK_MONTH_THRESHOLDS = [1, 3, 6, 9, 12];

const monthThresholdLabel = (months: number) =>
    months === 1 ? "≥ 1 month" : `≥ ${months} months`;

const saveButtonProps = {
    size: "sm" as const,
    colorScheme: "blue" as const,
    fontSize: "xs",
    h: "32px",
    minH: "32px",
    px: 3,
};

export const InactiveOrderReminderSettingsSection: FC = () => {
    const toast = useToast();

    const [loading, setLoading] = useState(true);
    const [savingConfig, setSavingConfig] = useState(false);
    const [savingEmail, setSavingEmail] = useState(false);

    const [allowedMonthThresholds, setAllowedMonthThresholds] = useState<number[]>(FALLBACK_MONTH_THRESHOLDS);
    const [defaultMonthsThreshold, setDefaultMonthsThreshold] = useState("6");

    const [subject, setSubject] = useState(PLAIN_SUBJECT_PREFIX);
    const [textBefore, setTextBefore] = useState(PLAIN_TEXT_BEFORE);
    const [textAfter, setTextAfter] = useState(PLAIN_TEXT_AFTER);
    const [previewDealershipName, setPreviewDealershipName] = useState("Sample Dealership LLC");
    const [emailHtml, setEmailHtml] = useState("");
    const [useCustomHtml, setUseCustomHtml] = useState(false);

    const monthOptions = useMemo(
        () => allowedMonthThresholds.map((value) => ({value: String(value), label: monthThresholdLabel(value)})),
        [allowedMonthThresholds],
    );

    const applyLoadedSettings = useCallback(
        (data: {
            inactiveOrderReminderEmailSubject?: string;
            inactiveOrderReminderEmailBody?: string;
            inactiveOrderReminderUseCustomEmailBody?: boolean;
            inactiveOrderReminderDefaultMonthsThreshold?: number;
            inactiveOrderReminderAllowedMonthsThresholds?: number[];
        }) => {
            if (data.inactiveOrderReminderDefaultMonthsThreshold != null) {
                setDefaultMonthsThreshold(String(data.inactiveOrderReminderDefaultMonthsThreshold));
            }
            if (data.inactiveOrderReminderAllowedMonthsThresholds?.length) {
                setAllowedMonthThresholds(data.inactiveOrderReminderAllowedMonthsThresholds);
            }

            const emailBody = data.inactiveOrderReminderEmailBody ?? "";
            setSubject(
                data.inactiveOrderReminderEmailSubject?.trim()
                    ? subjectTemplateToPlain(data.inactiveOrderReminderEmailSubject)
                    : PLAIN_SUBJECT_PREFIX,
            );
            setUseCustomHtml(Boolean(data.inactiveOrderReminderUseCustomEmailBody));
            setEmailHtml(emailBody);
            const mailParts = splitInactiveOrderReminderEmailBodyToPlain(emailBody);
            setTextBefore(mailParts.textBefore);
            setTextAfter(mailParts.textAfter);
        },
        [],
    );

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const adminRes = await ApplicationSettingsRepositoryInstance.getAdmin();
            if (cancelled) return;

            if (!adminRes.None && adminRes.Some && adminRes.Some.stripePercentFee !== undefined) {
                applyLoadedSettings(adminRes.Some);
            } else {
                toast({
                    title: "Failed to load Order reminder settings",
                    status: "error",
                    duration: 4000,
                    isClosable: true,
                });
            }
            setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applyLoadedSettings]);

    const handleSaveConfig = async () => {
        const months = Number(defaultMonthsThreshold);
        if (!allowedMonthThresholds.includes(months)) {
            toast({title: "Select a valid default period", status: "warning"});
            return;
        }

        const body: InactiveOrderReminderConfigAdminUpdateDto = {
            inactiveOrderReminderDefaultMonthsThreshold: months,
        };

        setSavingConfig(true);
        const res = await ApplicationSettingsRepositoryInstance.updateInactiveOrderReminderConfig(body);
        setSavingConfig(false);

        if (res.None) {
            toast({title: "Save failed", status: "error", duration: 4000, isClosable: true});
            return;
        }
        if (res.Some) {
            applyLoadedSettings(res.Some);
        }
        toast({title: "Order reminder settings saved", status: "success", duration: 3000, isClosable: true});
    };

    const handleSaveEmail = async () => {
        const reminderEmailBody = useCustomHtml
            ? emailHtml.trim()
            : buildComposedEmailHtmlFromFormFields(textBefore, textAfter);

        if (!subject.trim() || !reminderEmailBody) {
            toast({title: "Fill in email subject and body", status: "warning"});
            return;
        }

        const body: InactiveOrderReminderMailAdminUpdateDto = {
            inactiveOrderReminderEmailSubject: subject.trim(),
            inactiveOrderReminderEmailBody: reminderEmailBody,
            inactiveOrderReminderUseCustomEmailBody: useCustomHtml,
        };

        setSavingEmail(true);
        const res = await ApplicationSettingsRepositoryInstance.updateInactiveOrderReminderMail(body);
        setSavingEmail(false);

        if (res.None) {
            toast({title: "Save failed", status: "error", duration: 4000, isClosable: true});
            return;
        }
        if (res.Some) {
            applyLoadedSettings(res.Some);
        }
        toast({title: "Email template saved", status: "success", duration: 3000, isClosable: true});
    };

    if (loading) {
        return <Text fontSize="sm" color="gray.500">Loading Order reminder settings...</Text>;
    }

    return (
        <Stack spacing={4}>
            <Heading size="xs" textTransform="uppercase" letterSpacing="wider" color="gray.600">
                Order reminder
            </Heading>

            <FormControl>
                <FormLabel fontSize="sm">Default inactive period (list &amp; pending count)</FormLabel>
                <RadioGroup value={defaultMonthsThreshold} onChange={setDefaultMonthsThreshold}>
                    <Stack direction="row" spacing={4} flexWrap="wrap">
                        {monthOptions.map((o) => (
                            <Radio key={o.value} value={o.value} size="sm">
                                <Text fontSize="sm">{o.label}</Text>
                            </Radio>
                        ))}
                    </Stack>
                </RadioGroup>
                <FormHelperText fontSize="xs">
                    Used when opening Order reminders and for the pending dealerships counter.
                </FormHelperText>
            </FormControl>

            <Flex justify="flex-end">
                <Button {...saveButtonProps} onClick={handleSaveConfig} isLoading={savingConfig}>
                    Save settings
                </Button>
            </Flex>

            <Divider/>

            <Text fontSize="sm" fontWeight="medium" color="gray.700">
                Email template
            </Text>

            <InactiveOrderReminderEmailEditor
                subject={subject}
                textBefore={textBefore}
                textAfter={textAfter}
                previewDealershipName={previewDealershipName}
                emailHtml={emailHtml}
                useCustomHtml={useCustomHtml}
                onSubjectChange={setSubject}
                onTextBeforeChange={setTextBefore}
                onTextAfterChange={setTextAfter}
                onPreviewDealershipNameChange={setPreviewDealershipName}
                onEmailHtmlChange={(html) => {
                    setEmailHtml(html);
                    setUseCustomHtml(true);
                }}
                onUseCustomHtmlChange={setUseCustomHtml}
            />

            <Flex justify="flex-end" pt={1}>
                <Button {...saveButtonProps} onClick={handleSaveEmail} isLoading={savingEmail}>
                    Save email
                </Button>
            </Flex>
        </Stack>
    );
};

export default InactiveOrderReminderSettingsSection;
